import { Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ISegment } from '../../models/segment.model';
import { MatChipInputEvent } from '@angular/material/chips';
import { AppState } from 'src/app/core/app.state';
import { Store } from '@ngrx/store';
import { AddSegment, AddSegmentSuccess, UpdateSegment, UpdateSegmentSuccess } from '../../store/segments.actions';
import { Actions, ofType } from '@ngrx/effects';
import { Subscription, take } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-add-segments',
  templateUrl: './add-segments.component.html',
  styleUrls: ['./add-segments.component.scss']
})
export class AddSegmentsComponent implements OnInit {
  form: FormGroup = new FormGroup({});
  selectedSegmentType: string = '';
  chips: string[] = [];
  multiChips: string[] = [];
  booleanValue: boolean = false;
  subscriptions: Subscription[] = [];
  constructor(
    private dialogRef: MatDialogRef<AddSegmentsComponent>,
    private fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA) public data: ISegment,
    private store: Store<AppState>,
    private actions$: Actions,
    private translateService: TranslateService
  ) { }

  ngOnInit(): void {
    this.form = this.fb.group({
      name: [this.data?.name ?? '', Validators.required],
      description: [this.data?.description ?? ''],
      status: [this.data?.status ?? ''],
      value_type: [this.data?.value_type ?? '', Validators.required],
      chips: [[this.chips], this.chipsValidator()],
      multiChips: [[this.multiChips], this.multiChipsValidator()],
      labelValue: [''],
    }),
    this.form.get('value_type')?.valueChanges.subscribe(value => {
      this.updateValidators(value);
    });
    const sub1 = this.actions$.pipe(ofType(AddSegmentSuccess, UpdateSegmentSuccess), take(1)).subscribe(() => this.closed());

    if (this.data){
      this.data.value_type === 'single-select' ? this.chips = [...this.data.value_options] : this.data.value_type === 'multi-select' ? this.multiChips = [...this.data.value_options] : this.data.value_type === 'text' ? this.form.get('labelValue')?.setValue(this.data.value_options) : null
    }

    this.subscriptions.push(sub1);
  }

  updateValidators(value_type: string): void {
    if (value_type === 'text') {
      this.form.get('labelValue')?.setValidators(Validators.required);
    } else {
      this.form.get('labelValue')?.clearValidators();
    }
    this.form.get('chips')?.updateValueAndValidity();
    this.form.get('multiChips')?.updateValueAndValidity();
    this.form.get('labelValue')?.updateValueAndValidity();
  }

  chipsValidator(): any {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.parent && control.parent.get('value_type')?.value && control.parent.get('value_type')?.value === 'single-select') {
        return control.value.length === 0 ? { noChips: true } : null;
      }
      return null;
    };
  }

  multiChipsValidator(): any {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.parent && control.parent.get('value_type')?.value && control.parent.get('value_type')?.value === 'multi-select') {
        return control.value.length === 0 ? { noMultiChips: true } : null;
      }
      return null;
    };
  }


  addChip(event: MatChipInputEvent): void {
    const value = event.value.trim();
    if (value) {
      this.chips.push(value);
      this.form.get('chips')?.setValue(this.chips);
      event.input.value = '';
    }
  }

  removeChip(chip: string): void {
    const index = this.chips.indexOf(chip);
    if (index >= 0) {
      this.chips.splice(index, 1);
      this.form.get('chips')?.setValue(this.chips);
    }
  }

  addMultiChip(event: MatChipInputEvent): void {
    const value = event.value.trim();
    if (value) {
      this.multiChips.push(value);
      this.form.get('multiChips')?.setValue(this.multiChips);
      event.input.value = '';
    }
  }

  removeMultiChip(chip: string): void {
    const index = this.multiChips.indexOf(chip);
    if (index >= 0) {
      this.multiChips.splice(index, 1);
      this.form.get('multiChips')?.setValue(this.multiChips);
    }
  }

  closed() {
    this.dialogRef.close();
  }

  save(): boolean | void {  
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return false;
    } 
    const payload: Omit<ISegment, 'id'> = {
      name: this.form.get('name')?.value,
      description: this.form.get('description')?.value,
      status: this.form.get('status')?.value,
      value_type: this.form.get('value_type')?.value,
      value_options: this.form.get('value_type')?.value === 'single-select' ?  this.chips : this.form.get('value_type')?.value === 'multi-select' ? this.multiChips : this.form.get('value_type')?.value === 'text' ? this.form.get('labelValue')?.value : null,
    }
    if(this.data){
      this.store.dispatch(UpdateSegment({id: this.data.id, segment: payload}));
    }else{
      this.store.dispatch(AddSegment({segment: payload}));
    }
  }
  public getStatusTranslation(status: string): string {
    return this.translateService.instant(`segment.segmentsStatus.${status}`) || status;
  }
  ngOnDestroy(): void{
    this.subscriptions.forEach((sub => sub.unsubscribe()))
  }
}
